import { Table } from 'antd'
import { useContext, useLayoutEffect, useRef, useState } from 'react'
import { ChartFullScreenContext } from './MainWrapper'
import { sumColumnWidths } from './Table.utils'
import './chart.style.less'

const CARD_TABLE_Y = 280
const TABLE_HEADER_OFFSET = 55

interface props {
  title: string
  data?: any
  id: string
  periods?: string[]
  onPeriodChange?: (period: string) => any
}

function TableChart(props: props) {
  const { data } = props
  const rows = data?.row || []
  const columns = data?.chart_value || []
  const isFullScreen = useContext(ChartFullScreenContext)
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollY, setScrollY] = useState(CARD_TABLE_Y)
  const [cardWidth, setCardWidth] = useState(0)
  const contentWidth = sumColumnWidths(columns)
  // Avoid phantom x-scrollbar: only set scroll.x when columns overflow the card.
  const overflowsX = cardWidth > 0 && contentWidth > cardWidth
  const tableColumns = overflowsX
    ? columns
    : columns.map((column) => {
        if (!column?.fixed) return column
        const { fixed: _, ...rest } = column
        return rest
      })
  const recordCount = rows.length
  const showFooter = recordCount > 0

  useLayoutEffect(() => {
    const node = containerRef.current
    if (!node) return

    const measure = () => {
      const width = node.clientWidth
      const height = node.clientHeight
      setCardWidth(width)

      const header =
        (node.querySelector('.ant-table-header') as HTMLElement | null) ||
        (node.querySelector('.ant-table-thead') as HTMLElement | null)
      const headerHeight = header
        ? Math.ceil(header.getBoundingClientRect().height)
        : TABLE_HEADER_OFFSET
      const footer = node.querySelector(
        '.table-chart-footer'
      ) as HTMLElement | null
      const footerHeight = footer ? footer.clientHeight : 0

      // Body fills the rest of the wrap so the table is full height.
      const next = Math.floor(height - headerHeight - footerHeight)
      if (next > 0) setScrollY(next)
    }

    measure()
    const rafId = requestAnimationFrame(() => {
      measure()
      requestAnimationFrame(measure)
    })

    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => {
      cancelAnimationFrame(rafId)
      observer.disconnect()
    }
  }, [isFullScreen, rows.length, columns, overflowsX, showFooter])

  return (
    <div className="table-chart" ref={containerRef}>
      <Table
        rowKey="key"
        size="small"
        bordered
        columns={tableColumns}
        dataSource={rows}
        pagination={false}
        scroll={{
          y: scrollY,
          ...(overflowsX ? { x: contentWidth } : {}),
        }}
        tableLayout="fixed"
      />
      {showFooter && (
        <div className="table-chart-footer">
          <span>
            {recordCount} {recordCount === 1 ? 'record' : 'records'}
          </span>
        </div>
      )}
    </div>
  )
}

export default TableChart
